const PropTypes = require('prop-types');
const React = require('react');
const EditableCreditCard = require('./editable_credit_card.jsx');
const UrlHelpers = require('components/shared/url_helpers.js');
const UiUtil = require('components/utils/ui.js');
const CreditCardLoadingPlaceholder = require('./credit_card_loading_placeholder.jsx');
const CreditCards = require('components/credit_cards/credit_cards.js');
const CreditCard = require('components/credit_cards/credit_card.js');
const { UserStore, MobileDetection } = require('@reverbdotcom/discovery-ui');

class CreditCardList extends React.Component {
  constructor(props) {
    super(props);

    const hasFormCCErrors = props.formCreditCardErrors != null;

    this.state = {
      creditCardsFetched: false,
      creditCards: new CreditCards([]),
      countriesForSelect: [],
      addingNewCreditCard: false,
      primaryCardId: props.primaryCardId,
      initialSelectedCreditCardId: props.selectedCreditCardId,
      formCreditCardErrors: props.formCreditCardErrors,
      nowEditingCreditCardId: hasFormCCErrors ? props.selectedCreditCardId : null,
    };
  }

  componentDidMount() {
    this.fetchCountriesForSelect();
    this.fetchCreditCards();
  }

  handleFormSave() {
    this.setState({
      initialSelectedCreditCardId: null,
      formCreditCardErrors: null,
      creditCards: new CreditCards([]),
      creditCardsFetched: false,
    });
    if (MobileDetection.isMobileScreenWidth()) {
      UiUtil.scrollToElement(this.refs.creditCardListRoot);
    }
    this.fetchCreditCards();
  }

  fetchCreditCards() {
    Reverb.APIRequest.V3.get(
      UrlHelpers.myCreditCardsIndexPath
    ).done((response) => {
      this.setState({
        creditCards: new CreditCards(response.credit_cards),
        creditCardsFetched: true,
        addingNewCreditCard: false,
      });
    });
  }

  fetchCountriesForSelect() {
    Reverb.APIRequest.V3.get(
      UrlHelpers.countriesPath
    ).done((response) => {
      this.setState({
        countriesForSelect: response.countries,
      });
    });
  }

  isLoading() {
    // the countries list has not yet loaded from the API
    return (this.state.countriesForSelect.length === 0 || !this.state.creditCardsFetched);
  }

  isSelected(creditCard, index) {
    if (this.state.initialSelectedCreditCardId && this.selectedCreditCard()) {
      return creditCard.creditCardId === this.state.initialSelectedCreditCardId;
    }

    // When creating a new credit card, the blank form is selected,
    // and none of the persisted CCs are selected.
    if (this.isCreatingCreditCard()) {
      return false;
    }

    return index === 0;
  }

  formErrorForCreditCard(creditCard) {
    if (creditCard.creditCardId === this.state.initialSelectedCreditCardId) {
      return this.state.formCreditCardErrors;
    }
    return null;
  }

  isEditing(creditCard) {
    return creditCard.creditCardId === this.state.nowEditingCreditCardId;
  }

  selectedCreditCard() {
    return this.state.creditCards.findById(this.state.initialSelectedCreditCardId);
  }

  nonSelectedCreditCards() {
    return this.state.creditCards.nonSelectedCreditCards(this.state.initialSelectedCreditCardId);
  }

  creditCardsInOrder() {
    return this.state.creditCards.creditCardsInOrder(this.state.initialSelectedCreditCardId);
  }

  // (a workaround to have the proper card to appear selected/highlighted)
  // boolean flags are added to the key to force rerendering of the credit card
  keyForEditableCreditCard(creditCard, index) {
    return `${creditCard.creditCardId}-${this.isCreatingCreditCard()}-${this.isSelected(creditCard, index)}`;
  }

  persistedCreditCards() {
    return (
      this.creditCardsInOrder().map((creditCard, index) =>
        <EditableCreditCard
          key={this.keyForEditableCreditCard(creditCard, index)}
          creditCard={creditCard}
          clientToken={this.props.clientToken}
          formErrors={this.formErrorForCreditCard(creditCard)}
          countriesForSelect={this.state.countriesForSelect}
          formObjectName={this.props.formObjectName}
          selectable={this.props.selectable}
          selected={this.isSelected(creditCard, index)}
          primaryCardId={this.state.primaryCardId}
          editing={this.isEditing(creditCard)}
          handleUpdate={() => this.handleFormSave()}
          updatePrimaryCard={(cardId) => this.setState({ primaryCardId: cardId })}
          displayCancelEditLink
          closeNewCreditCardForm={() => {
            this.setState({ addingNewCreditCard: false });
          }}
          handleEditing={(creditCardId) => {
            this.setState({ nowEditingCreditCardId: creditCardId, addingNewCreditCard: false });
          }}
        />
      )
    );
  }

  userCountryCode() {
    return UserStore.getState().countryCode;
  }

  get blankCreditCard() {
    const cardParams = {
      address: {
        country_code: this.userCountryCode(),
      },
      expiration_month: '01',
      full_expiration_year: new Date().getFullYear().toString(),
    };
    return new CreditCard(cardParams);
  }

  blankCreditCardForm() {
    if (this.isCreatingCreditCard()) {
      return (
        <EditableCreditCard
          countriesForSelect={this.state.countriesForSelect}
          handleUpdate={() => this.handleFormSave()}
          creditCard={this.blankCreditCard}
          selectable={this.props.selectable}
          formObjectName={this.props.formObjectName}
          clientToken={this.props.clientToken}
          handleEditing={(creditCardId) => this.setState({ nowEditingCreditCardId: creditCardId })}
          displayCancelEditLink
          primaryCardId={this.state.primaryCardId}
          selected
          editing
        />
      );
    }
    return null;
  }

  hasCreditCards() {
    return this.state.creditCards.hasCards;
  }

  isCreatingCreditCard() {
    if (this.props.selectable) {
      return !this.hasCreditCards() || this.state.addingNewCreditCard;
    }
    return this.state.addingNewCreditCard;
  }

  handleAddNewCreditCardClick() {
    this.setState({
      addingNewCreditCard: true,
      nowEditingCreditCardId: null,
    });
  }

  addCreditCardButton(buttonClasses) {
    return (
      <a
        id="add_new_credit_card"
        className={buttonClasses}
        onClick={() => this.handleAddNewCreditCardClick()}
      >
        {I18n.t('js.creditCards.creditCardList.addNewCard')}
      </a>
    );
  }

  addCreditCardEmptyState() {
    return (
      <div className="align-center">
        <p className="mtb-1 muted">
          {I18n.t('js.creditCards.creditCardList.noCardsOnFile')}
        </p>
        {this.addCreditCardButton('button button--orange')}
      </div>
    );
  }

  addNewCreditCardAction() {
    if (this.isCreatingCreditCard()) { return null; }

    if (this.props.selectable || this.hasCreditCards()) {
      return this.addCreditCardButton('button button--blue mobile-width-100');
    }

    if (!this.props.selectable && !this.hasCreditCards()) {
      return this.addCreditCardEmptyState();
    }

    return null;
  }

  render() {
    if (this.isLoading()) {
      return (
        <div ref="creditCardListRoot">
          <CreditCardLoadingPlaceholder />
        </div>
      );
    }

    return (
      <div ref="creditCardListRoot">
        <ul>
          {this.persistedCreditCards()}
          {this.blankCreditCardForm()}
        </ul>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12 mobile-mb-1">
            {this.addNewCreditCardAction()}
          </div>
        </div>
      </div>
    );
  }
}

CreditCardList.propTypes = {
  clientToken: PropTypes.string.isRequired,
  formObjectName: PropTypes.string,
  selectable: PropTypes.bool.isRequired,
  selectedCreditCardId: PropTypes.string,
  formCreditCardErrors: PropTypes.string,
  primaryCardId: PropTypes.string.isRequired,
};

module.exports = CreditCardList;



// WEBPACK FOOTER //
// ./javascripts/components/credit_cards/credit_card_list.jsx